<template>
  <v-card flat>
    <!-- Скелетон для загрузки -->
    <template v-if="isLoading">
      <v-card-text class="form-wrapper">
        <div v-for="i in 4" :key="i" class="form-row">
          <div class="form-label">
            <v-skeleton-loader type="text" width="100" />
          </div>
          <v-skeleton-loader type="text" width="100%" />
        </div>
        <div class="form-row">
          <v-skeleton-loader type="card" width="100%" />
        </div>
      </v-card-text>
    </template>

    <!-- Ошибка -->
    <template v-else-if="hasError">
      <v-card-text>
        <v-alert
          type="error"
          variant="tonal"
          closable
          @click:close="hasError = false"
        >
          {{ errorMessage || "Ошибка загрузки данных" }}
        </v-alert>
      </v-card-text>
    </template>

    <!-- Основной контент -->
    <template v-else>
      <v-card-text class="form-wrapper">
        <div class="form-row">
          <div class="form-label"></div>
          <div class="chek-info">
            <span
              v-if="student.target && student.target !== 0"
              class="target-label"
            >
              Поступил по целевому
            </span>
          </div>
        </div>

        <div class="form-row">
          <div class="form-label">Целевая организация</div>
          <v-autocomplete
            v-model="student.target"
            :items="targetOrganizations"
            item-title="name"
            item-value="key"
            hide-details
            variant="outlined"
            density="compact"
            @update:modelValue="onFieldUpdate('target', $event)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">Группа здоровья</div>
          <v-autocomplete
            v-model="student.health_id"
            :items="healthGroups"
            item-title="name"
            item-value="key"
            variant="outlined"
            density="compact"
            @update:modelValue="onFieldUpdate('health_id', $event)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">Физкультурная группа</div>
          <v-autocomplete
            v-model="student.ph_group"
            :items="physicalGroup"
            item-title="text"
            item-value="value"
            hide-details
            variant="outlined"
            density="compact"
            @update:modelValue="onFieldUpdate('ph_group', $event)"
          />
        </div>

        <div class="form-row privileges">
          <div class="form-label">Привилегии</div>
          <div class="checkbox-list">
            <v-checkbox
              v-for="privilege in privilegeGroups"
              :key="privilege.key"
              :label="privilege.name"
              :value="privilege.key"
              v-model="selectedPrivilegeGroups"
              hide-details
              density="compact"
              @update:modelValue="onPrivilegesUpdate"
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-label">Нуждается ли в общежитии?</div>
          <v-select
            v-model="student.hostel"
            :items="hostelOptions"
            item-title="text"
            item-value="value"
            label="Общежитие"
            hide-details
            variant="outlined"
            density="compact"
            @update:modelValue="onFieldUpdate('hostel', $event)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">Военкомат</div>
          <v-autocomplete
            v-model="student.military_office_id"
            :items="militaryOffices"
            item-title="name"
            item-value="key"
            hide-details
            variant="outlined"
            density="compact"
            @update:modelValue="onFieldUpdate('military_office_id', $event)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">В списке нет нужного военкомата</div>
          <v-text-field
            v-model="student.military_office"
            hide-details
            variant="outlined"
            density="compact"
            @input="onFieldUpdate('military_office', $event.target.value)"
          />
        </div>

        <div class="form-row">
          <div class="form-label"></div>
          <v-btn
            append-icon="mdi-plus"
            size="small"
            variant="text"
            color="primary"
            :href="
              $model.wwwroot +
              '/blocks/manage_groups/#/dictionaries/MilitaryOffice'
            "
            target="_blank"
          >
            <template #append>
              <v-icon color="success" />
            </template>
            Добавить новый военкомат
          </v-btn>
        </div>

        <div class="form-row">
          <div class="form-label">Место работы (при наличии)</div>
          <v-text-field
            v-model="student.work"
            hide-details
            variant="outlined"
            density="compact"
            @input="onFieldUpdate('work', $event.target.value)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">Должность</div>
          <v-text-field
            v-model="student.position"
            hide-details
            variant="outlined"
            density="compact"
            @input="onFieldUpdate('position', $event.target.value)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">Стаж</div>
          <v-text-field
            v-model="student.experience"
            hide-details
            variant="outlined"
            density="compact"
            @input="onFieldUpdate('experience', $event.target.value)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">Примечание</div>
          <v-textarea
            v-model="student.notes"
            @input="onFieldUpdate('notes', $event.target.value)"
          />
        </div>
      </v-card-text>
    </template>
  </v-card>
</template>

<script>
export default {
  name: "AdditionalInfo",

  props: {
    student: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      hasError: false,
      errorMessage: null,

      targetOrganizations: [],
      healthGroups: [],
      privilegeGroups: [],
      selectedPrivilegeGroups: [],
      militaryOffices: [],
      facilities: [],

      physicalGroup: [
        { value: 1, text: "Основная" },
        { value: 2, text: "Подготовительная" },
        { value: 3, text: "Специальная" },
      ],

      hostelOptions: [
        { text: "Нет", value: 0 },
        { text: "Да", value: 1 },
        { text: "На период сессии", value: 2 },
      ],
    };
  },

  created() {
    this.loadData();
  },

  methods: {
    loadData() {
      this.isLoading = true;
      this.hasError = false;
      this.errorMessage = null;

      const promises = [this.loadDirectory(), this.getPersonalFacilities()];

      Promise.allSettled(promises)
        .then((results) => {
          const hasError = results.some((r) => r.status === "rejected");
          if (hasError) {
            this.hasError = true;
            this.errorMessage = "Ошибка загрузки данных";
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    loadDirectory() {
      return this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/core_procollege/helpers/DirectoryLoader/Load",
          new URLSearchParams({
            directory: JSON.stringify([
              "targetOrganizations",
              "healthGroups",
              "privilegeGroups",
              "militaryOffice",
            ]),
          })
        )
        .then((res) => {
          this.targetOrganizations = [];
          for (let i in res.data.targetOrganizations) {
            this.targetOrganizations.push({
              key: res.data.targetOrganizations[i].id,
              name: res.data.targetOrganizations[i].name,
            });
          }

          this.healthGroups = [];
          for (let i in res.data.healthGroups) {
            this.healthGroups.push({
              key: res.data.healthGroups[i].code,
              name: res.data.healthGroups[i].name,
            });
          }

          this.privilegeGroups = [];
          for (let i in res.data.privilegeGroups) {
            this.privilegeGroups.push({
              key: Number(res.data.privilegeGroups[i].id),
              name: res.data.privilegeGroups[i].name,
            });
          }

          this.militaryOffices = [];
          for (let i in res.data.militaryOffice) {
            this.militaryOffices.push({
              key: Number(res.data.militaryOffice[i].id),
              name: res.data.militaryOffice[i].name,
            });
          }
        })
        .catch((error) => {
          console.error("Ошибка загрузки справочников:", error);
          throw error;
        });
    },

    getPersonalFacilities() {
      if (!this.$route.params.personal_id) {
        return Promise.resolve();
      }

      return this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/manage_groups/secretariat/studentCard/getPersonalFacilities",
          new URLSearchParams({
            personal_id: this.$route.params.personal_id,
          })
        )
        .then((res) => {
          this.facilities = res.data || [];
          this.selectedPrivilegeGroups = this.facilities.map(
            (f) => f.facilities_id
          );
        })
        .catch((error) => {
          console.error("Ошибка при загрузке привилегий:", error);
          this.facilities = [];
          throw error;
        });
    },

    onFieldUpdate(field, value) {
      this.$emit("update:student", {
        ...this.student,
        [field]: value,
      });
    },

    onPrivilegesUpdate() {
      this.$emit("update:student", {
        ...this.student,
        privileges: this.selectedPrivilegeGroups,
      });
    },
  },
};
</script>

<style scoped>
.checkbox-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}

.target-label {
  color: #4caf50;
  font-weight: 600;
  font-size: 14px;
  background: #e8f5e9;
  padding: 4px 12px;
  border-radius: 4px;
}

::v-deep(.v-checkbox .v-selection-control__input),
:deep(.v-radio .v-selection-control__input) {
  color: rgba(5, 55, 135, 0.8) !important;
}
</style>